import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';

const sortByDateDesc = (items = []) =>
  [...items].sort((a, b) => {
    const aDate = a.createdAt?.toMillis?.() || 0;
    const bDate = b.createdAt?.toMillis?.() || 0;
    return bDate - aDate;
  });

const computeRemaining = (invoice = {}) => {
  if (typeof invoice.remaining === 'number') return Number(invoice.remaining);
  const total = Number(invoice.total || 0);
  const deposit = Number(invoice.deposit || 0);
  const discount = Number(invoice.discount || 0);
  const payments = (invoice.payments || []).reduce(
    (sum, entry) => sum + Number(entry.amount || 0),
    0
  );
  return Math.max(total - deposit - discount - payments, 0);
};

const decodePathValue = (value = '') => {
  try {
    return decodeURIComponent(value);
  } catch (err) {
    return value;
  }
};

const cleanInvoiceFileName = (value = '') => value.replace(/\.pdf$/i, '').trim();

const extractInvoiceFromAttachments = (attachments = []) => {
  if (!Array.isArray(attachments)) return null;
  for (const file of attachments) {
    if (!file) continue;
    const raw = file.storagePath || file.path || file.url || file.href || '';
    if (!raw) continue;
    const decoded = decodePathValue(raw);
    const match = decoded.match(/invoices\/([^/]+)\/([^/?#]+)/i);
    if (!match) continue;
    return {
      clientSegment: match[1],
      invoiceId: cleanInvoiceFileName(match[2]),
    };
  }
  return null;
};

const inferInvoiceContext = (task = {}) => {
  if (!task) return null;
  const invoiceIdCandidate =
    task.relatedInvoiceId ||
    task.metadata?.relatedInvoiceId ||
    task.metadata?.invoiceId ||
    task.metadata?.invoice?.id;
  const emailCandidate =
    task.client?.email ||
    task.metadata?.clientEmail ||
    task.clientEmail ||
    task.customerEmail;

  if (invoiceIdCandidate && emailCandidate) {
    return { invoiceId: invoiceIdCandidate, clientEmail: emailCandidate.toLowerCase() };
  }

  if (invoiceIdCandidate && task.clientId) {
    return { invoiceId: invoiceIdCandidate, clientDocId: task.clientId };
  }

  const attachmentInfo = extractInvoiceFromAttachments(task.attachments);
  if (attachmentInfo) {
    return {
      invoiceId: invoiceIdCandidate || attachmentInfo.invoiceId,
      clientDocId: attachmentInfo.clientSegment,
      clientStorageKey: attachmentInfo.clientSegment,
    };
  }
  return null;
};

const WarehouseDashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState('');
  const [actionFeedback, setActionFeedback] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [hydrating, setHydrating] = useState(false);
  const hydratedTasksRef = useRef(new Set());
  const callableRef = useRef(null);

  const ensureCallable = () => {
    if (!callableRef.current) {
      callableRef.current = httpsCallable(functions, 'fetchWarehouseInvoiceDetails');
    }
    return callableRef.current;
  };

  const buildTaskRequest = useCallback((task) => {
    if (!task) return null;
    const cacheKey = task.docId || task.id;
    if (!cacheKey) return null;
    const context = inferInvoiceContext(task) || {};
    return {
      taskId: cacheKey,
      clientId:
        task.clientId ||
        context.clientDocId ||
        task.metadata?.clientDocId ||
        task.metadata?.clientId ||
        null,
      clientEmail:
        context.clientEmail ||
        task.client?.email ||
        task.metadata?.clientEmail ||
        task.clientEmail ||
        null,
      relatedInvoiceId:
        task.relatedInvoiceId ||
        context.invoiceId ||
        task.metadata?.invoiceId ||
        task.metadata?.relatedInvoiceId ||
        null,
      clientStorageKey: context.clientStorageKey || task.metadata?.clientStorageKey || null,
    };
  }, []);

  const fetchWarehouseDetails = useCallback(
    async (entries = []) => {
      if (!entries.length) return [];
      try {
        const callable = ensureCallable();
        const response = await callable({ tasks: entries });
        const data = response?.data?.tasks || response?.data || [];
        if (!Array.isArray(data)) {
          return [];
        }
        return data;
      } catch (err) {
        console.error('❌ No se pudieron cargar los detalles de almacén:', err);
        throw err;
      }
    },
    []
  );

  const hydrateTasks = useCallback(
    async (baseTasks = []) => {
      const pending = baseTasks.filter((task) => {
        if (!task) return false;
        const cacheKey = task.docId || task.id;
        if (hydratedTasksRef.current.has(cacheKey)) return false;
        const hasClient = Boolean(task.client?.name && task.client?.email);
        const hasItems = Array.isArray(task.metadata?.items) && task.metadata.items.length > 0;
        const hasInvoiceRef = Boolean(
          task.relatedInvoiceId || task.metadata?.invoiceId || task.metadata?.relatedInvoiceId
        );
        return !hasClient || !hasItems || !hasInvoiceRef;
      });
      if (!pending.length) return;
      setHydrating(true);
      console.groupCollapsed('[WarehouseDashboard] Hidratando tareas');
      console.log('Tareas en cola:', pending.map((task) => task.docId || task.id));

      try {
        const payloads = pending
          .map(buildTaskRequest)
          .filter((entry) => entry && entry.relatedInvoiceId);
        if (!payloads.length) {
          console.log('Sin datos suficientes para hidratar.');
          console.groupEnd();
          setHydrating(false);
          return;
        }
        const updates = await fetchWarehouseDetails(payloads);
        if (!updates.length) {
          console.log('No se recibieron datos para hidratar.');
          console.groupEnd();
          setHydrating(false);
          payloads.forEach((entry) => hydratedTasksRef.current.add(entry.taskId));
          return;
        }
        const map = new Map(updates.map((entry) => [entry.taskId, entry]));
        setTasks((prev) =>
          prev.map((task) => {
            const patch = map.get(task.docId) || map.get(task.id);
            if (!patch) return task;
            return {
              ...task,
              clientId: task.clientId || patch.clientId,
              relatedInvoiceId: task.relatedInvoiceId || patch.relatedInvoiceId,
              client: patch.client ? { ...task.client, ...patch.client } : task.client,
              metadata: patch.metadata
                ? { ...(task.metadata || {}), ...(patch.metadata || {}) }
                : task.metadata,
              invoiceSnapshot: patch.invoice || task.invoiceSnapshot,
            };
          })
        );
        updates.forEach((entry) => {
          if (entry.taskId) {
            hydratedTasksRef.current.add(entry.taskId);
          }
        });
      } catch (err) {
        console.error('❌ Error hidratando tareas:', err);
        setActionFeedback({
          type: 'error',
          message:
            'No pudimos sincronizar los detalles de las tareas. Intenta refrescar o vuelve a iniciar sesión.',
        });
      } finally {
        console.groupEnd();
        setHydrating(false);
      }
    },
    [buildTaskRequest, fetchWarehouseDetails]
  );

  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const warehouseQuery = query(
          collection(db, 'operations_tasks'),
          where('type', '==', 'warehouse')
        );
        const snap = await getDocs(warehouseQuery);
        const results = snap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            ...data,
            docId: docSnap.id,
            displayId: data.id || docSnap.id,
            rawId: data.id,
          };
        });
        const sorted = sortByDateDesc(results);
        setTasks(sorted);
      } catch (err) {
        console.error('❌ Error cargando tareas de almacén:', err);
        setError('No pudimos cargar las tareas. Intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, [hydrateTasks]);

  useEffect(() => {
    if (!loading && tasks.length) {
      hydrateTasks(tasks);
    }
  }, [tasks, hydrateTasks, loading]);

  const stats = useMemo(() => {
    const totals = {
      pending: 0,
      in_progress: 0,
      blocked: 0,
      completed: 0,
    };
    tasks.forEach((task) => {
      const key = task.status || 'pending';
      if (totals[key] !== undefined) {
        totals[key] += 1;
      }
    });
    return totals;
  }, [tasks]);

  const upcoming = useMemo(() => {
    const now = Date.now();
    const threeDays = 3 * 24 * 60 * 60 * 1000;
    return tasks
      .filter((task) => {
        const due = task.dueDate?.toMillis?.() || task.dueDate?.seconds * 1000;
        return due && due - now <= threeDays && task.status !== 'completed';
      })
      .slice(0, 5);
  }, [tasks]);

  const backlog = useMemo(() => tasks.filter((task) => task.status !== 'completed'), [tasks]);

  const updateLocalTask = (taskId, updates) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.docId === taskId || task.id === taskId ? { ...task, ...updates } : task
      )
    );
  };

  const handleStatusChange = async (taskLike, status, options = {}) => {
    let normalizedTaskId = '';

    if (typeof taskLike === 'string' && taskLike.trim()) {
      normalizedTaskId = taskLike.trim();
    } else if (typeof taskLike === 'object' && taskLike) {
      normalizedTaskId =
        taskLike.docId || taskLike.id || taskLike.rawId || taskLike.relatedOrderId || '';
    }

    if (!normalizedTaskId || !normalizedTaskId.trim()) {
      console.error('⚠️ handleStatusChange recibió un taskId inválido:', taskLike);
      normalizedTaskId = '';
    } else {
      normalizedTaskId = normalizedTaskId.trim();
    }

    if (!normalizedTaskId) {
      setActionFeedback({
        type: 'error',
        message: 'No encontramos el ID de la tarea. Refresca la vista e intenta nuevamente.',
      });
      return;
    }
    const { timelineNote: providedNote = '', skipPrompt = false } = options;
    let timelineNote = providedNote;
    const needsNote = status === 'completed' || status === 'in_progress';
    if (!skipPrompt && needsNote) {
      const promptText =
        status === 'in_progress'
          ? 'Notas al recibir el pedido (ej. piezas verificadas, incidencias):'
          : 'Notas para instalación / entrega (ej. medidas confirmadas, empaquetado, incidencias):';
      timelineNote = window.prompt(promptText, timelineNote) || timelineNote;
    }
    if (!timelineNote && status === 'in_progress') {
      timelineNote = 'Pedido recibido en almacén.';
    }
    if (!timelineNote && status === 'completed') {
      timelineNote = 'Pedido listo para instalación / entrega.';
    }
    setUpdatingId(normalizedTaskId);
    setActionFeedback(null);
    try {
      const callable = httpsCallable(functions, 'updateOperationalTaskStatus');
      await callable({ taskId: normalizedTaskId, status, timelineNote });
      updateLocalTask(normalizedTaskId, { status });
      setActionFeedback({ type: 'success', message: 'Estado actualizado correctamente.' });
    } catch (err) {
      console.error('❌ Error actualizando tarea de almacén:', err);
      setActionFeedback({
        type: 'error',
        message: err.message || 'No pudimos actualizar esta tarea. Intenta nuevamente.',
      });
    } finally {
      setUpdatingId('');
      setTimeout(() => setActionFeedback(null), 4000);
    }
  };

  const resetInvoiceState = () => {
    setInvoiceInfo(null);
    setInvoiceError(null);
    setInvoiceLoading(false);
  };

  const closeModal = () => {
    setSelectedTask(null);
    resetInvoiceState();
  };

  const loadInvoice = useCallback(
    async (task) => {
      if (!task) return;
      const payload = buildTaskRequest(task);
      if (!payload?.relatedInvoiceId) {
        setInvoiceError(
          'No se especificó la factura asociada. Edita la tarea en Operaciones y vincula un ID de factura.'
        );
        setInvoiceInfo(null);
        return;
      }
      setInvoiceLoading(true);
      setInvoiceError(null);
      try {
        const [detail] = await fetchWarehouseDetails([payload]);
        if (!detail || !detail.invoice) {
          setInvoiceError('No encontramos información de la factura asociada.');
          setInvoiceInfo(null);
          return;
        }
        setInvoiceInfo(detail.invoice);
        setSelectedTask((prev) => {
          if (!prev || prev.docId !== task.docId) return task;
          return {
            ...prev,
            client: detail.client ? { ...prev.client, ...detail.client } : prev.client,
            metadata: detail.metadata
              ? { ...(prev.metadata || {}), ...(detail.metadata || {}) }
              : prev.metadata,
            invoiceSnapshot: detail.invoice,
          };
        });
      } catch (err) {
        console.error('❌ Error cargando factura:', err);
        setInvoiceError('No pudimos obtener la factura asociada.');
        setInvoiceInfo(null);
      } finally {
        setInvoiceLoading(false);
      }
    },
    [buildTaskRequest, fetchWarehouseDetails]
  );

  const openTaskModal = (task) => {
    setSelectedTask(task);
    resetInvoiceState();
    loadInvoice(task);
  };


  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-emerald-900">Almacén & Producción</h1>
        <p className="text-sm text-emerald-700">
          Controla la preparación de pedidos, abastecimiento y tareas en espera.
        </p>
      </div>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>}

      <section className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Pendientes', value: stats.pending, tone: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'En proceso', value: stats.in_progress, tone: 'text-sky-600', bg: 'bg-sky-50' },
          { label: 'Bloqueadas', value: stats.blocked, tone: 'text-rose-600', bg: 'bg-rose-50' },
          { label: 'Completadas', value: stats.completed, tone: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map((card) => (
          <article
            key={card.label}
            className={`rounded-2xl border border-white ${card.bg} p-4 shadow-sm`}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-800">
              {card.label}
            </p>
            <p className={`mt-2 text-3xl font-bold ${card.tone}`}>{card.value}</p>
          </article>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-emerald-900">Tareas próximas (72h)</h2>
          {loading ? (
            <p className="text-sm text-gray-500">Cargando…</p>
          ) : upcoming.length === 0 ? (
            <p className="text-sm text-gray-500">No hay entregas próximas.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {upcoming.map((task) => (
                <li
                  key={task.id}
                  className="rounded-2xl border border-emerald-50 bg-emerald-50/60 px-4 py-3 text-sm shadow-sm"
                >
                  <p className="font-semibold text-emerald-900">{task.title || task.relatedOrderId}</p>
                  <p className="text-xs text-emerald-700">
                    Cliente: {task.client?.name || 'Sin asignar'} · Due:{' '}
                    {task.dueDate?.toDate?.()?.toLocaleString('es-PR', { dateStyle: 'medium' }) || 'Sin fecha'}
                  </p>
                  <button
                    type="button"
                    onClick={() => openTaskModal(task)}
                    className="mt-2 text-xs font-semibold text-emerald-700 hover:underline"
                  >
                    Ver detalle
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-emerald-900">Inventario / checklist</h2>
          <ul className="space-y-3 text-sm text-gray-600">
            <li className="rounded-2xl border border-emerald-50 px-4 py-3">
              ✅ Confirmar perfiles y cristales para pedidos en curso.
            </li>
            <li className="rounded-2xl border border-emerald-50 px-4 py-3">
              📦 Registrar ingreso de herrajes críticos y accesorios premium.
            </li>
            <li className="rounded-2xl border border-emerald-50 px-4 py-3">
              🧾 Adjuntar manuales, etiquetas y documentación de entrega.
            </li>
          </ul>
        </div>
      </section>

      <section className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-emerald-900">Backlog activo</h2>
            <p className="text-sm text-emerald-700">Ordena prioridades y registra comentarios.</p>
          </div>
          <div className="flex items-center gap-3">
            {hydrating && (
              <span className="text-xs font-semibold text-emerald-600">Sincronizando datos…</span>
            )}
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
              {backlog.length} pendientes
            </span>
          </div>
        </div>
        {actionFeedback && (
          <div
            className={`mt-3 rounded-2xl border px-4 py-2 text-sm ${
              actionFeedback.type === 'error'
                ? 'border-rose-200 bg-rose-50 text-rose-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {actionFeedback.message}
          </div>
        )}
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-emerald-50 text-sm">
            <thead className="bg-emerald-50 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              <tr>
                <th className="px-4 py-2 text-left">Orden</th>
                <th className="px-4 py-2 text-left">Cliente</th>
                <th className="px-4 py-2 text-left">Estado</th>
                <th className="px-4 py-2 text-left">Notas</th>
                <th className="px-4 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-emerald-50">
              {backlog.slice(0, 10).map((task) => (
                <tr key={task.docId || task.id}>
                  <td className="px-4 py-3 font-semibold text-emerald-900">
                    <p>{task.relatedOrderId || task.title || task.displayId || task.docId}</p>
                    <p className="text-xs font-normal text-gray-500">
                      {task.client?.projectLocation || 'Ubicación pendiente'}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <p className="font-semibold text-gray-800">{task.client?.name || 'Sin asignar'}</p>
                    {task.client?.email && (
                      <p className="text-xs text-gray-500">{task.client.email}</p>
                    )}
                    {task.client?.phone && (
                      <p className="text-xs text-gray-500">{task.client.phone}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 capitalize text-gray-600">{task.status || 'pending'}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {task.description || task.metadata?.notes || 'Sin notas'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {task.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleStatusChange(task, 'in_progress')}
                          disabled={updatingId === (task.docId || task.id)}
                          className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
                        >
                          {updatingId === (task.docId || task.id) ? 'Registrando…' : 'Confirmar recibido'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openTaskModal(task)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Ver detalles
                      </button>
                      {task.status !== 'completed' && (
                        <button
                          type="button"
                          onClick={() => handleStatusChange(task, 'completed')}
                          disabled={updatingId === (task.docId || task.id)}
                          className="rounded-full border border-emerald-200 bg-emerald-600/10 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                        >
                          {updatingId === (task.docId || task.id) ? 'Actualizando…' : 'Listo para instalación'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-emerald-50 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-emerald-500">
                  Orden {selectedTask.relatedOrderId || selectedTask.id}
                </p>
                <h3 className="text-2xl font-semibold text-emerald-900">
                  {selectedTask.client?.name || 'Cliente pendiente'}
                </h3>
                <p className="text-sm text-emerald-600">
                  {selectedTask.client?.projectLocation || 'Ubicación pendiente'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="text-sm font-semibold text-emerald-500 hover:text-emerald-800"
              >
                Cerrar
              </button>
            </div>

            {invoiceLoading ? (
              <p className="py-6 text-sm text-gray-500">Cargando factura asociada…</p>
            ) : invoiceError ? (
              <p className="py-6 text-sm text-rose-600">{invoiceError}</p>
            ) : invoiceInfo ? (
              <div className="mt-6 space-y-6">
                <section className="grid gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 text-sm text-emerald-900 md:grid-cols-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">Total</p>
                    <p className="text-2xl font-semibold">
                      ${Number(invoiceInfo.total || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">Depósito</p>
                    <p className="text-2xl font-semibold">
                      ${Number(invoiceInfo.deposit || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">
                      Balance pendiente
                    </p>
                    <p className="text-2xl font-semibold">
                      ${computeRemaining(invoiceInfo).toFixed(2)}
                    </p>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-100 bg-white p-4">
                  <h4 className="text-sm font-semibold text-slate-900">Pagos registrados</h4>
                  {(invoiceInfo.payments || []).length === 0 ? (
                    <p className="text-xs text-slate-500">No hay pagos registrados.</p>
                  ) : (
                    <ul className="mt-2 space-y-2 text-xs text-slate-600">
                      {(invoiceInfo.payments || []).map((entry, idx) => (
                        <li
                          key={`${invoiceInfo.id}-payment-${idx}`}
                          className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                        >
                          <p className="font-semibold text-slate-800">
                            ${Number(entry.amount || 0).toFixed(2)} · {entry.method || 'método'}
                          </p>
                          <p>
                            {entry.recordedAt?.toDate?.()?.toLocaleString('es-PR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            }) || ''}
                          </p>
                          {entry.notes && <p className="text-slate-500">{entry.notes}</p>}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-sm text-emerald-900">
                  <p>
                    El cobro final y la firma del cliente los gestiona el técnico asignado durante la
                    instalación. Usa esta vista para verificar totales y dejar listas las unidades.
                  </p>
                  {invoiceInfo.completionPdfUrl && (
                    <a
                      href={invoiceInfo.completionPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex text-xs font-semibold text-emerald-700 hover:underline"
                    >
                      Ver confirmación enviada por técnicos
                    </a>
                  )}
                </section>
              </div>
            ) : (
              <p className="py-6 text-sm text-gray-500">No se encontró la factura asociada.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseDashboard;
